import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  DisplayCodeableConceptSchema,
  PaginatedBundleSchema,
  PeriodSchema,
} from '../domain/schema';
import {
  ContactGetSchema,
  ContactSchema,
  GeneralSchema,
} from '../person/schema';

export const PractitionerQualificationCodeSchema = z.object({
  code: DisplayCodeableConceptSchema,
  issuer: DisplayReferenceSchema.optional(),
  period: PeriodSchema.optional(),
});
export type PractitionerQualificationCode = z.infer<
  typeof PractitionerQualificationCodeSchema
>;

export const QualificationFieldsSchema = z.object({
  qualificationCode: PractitionerQualificationCodeSchema,
  registrationNumber: z.string(),
});
export type QualificationFields = z.infer<typeof QualificationFieldsSchema>;

export const CreateQualificationFieldsSchema = z.object({
  qualificationCode: z.string(),
  registrationNumber: z.string(),
});

export const PractitionerQualificationUpdateRequestSchema =
  CreateQualificationFieldsSchema.extend({
    id: UUIDSchema,
  });

export type CreateQualificationFields = z.infer<
  typeof CreateQualificationFieldsSchema
>;

export const PractitionerExternalSchema = z.object({
  ampathMnemonic: z.string().optional(),
});
export const UpdatePractitionerExternalSchema =
  PractitionerExternalSchema.extend({
    id: UUIDSchema,
  });
export type External = z.infer<typeof PractitionerExternalSchema>;
export type UpdatePractitionerExternal = z.infer<
  typeof UpdatePractitionerExternalSchema
>;

export const PractitionerSettingsSchema = z.object({
  canPrescribe: z.boolean().optional(),
});
export const UpdatePractitionerSettingsSchema =
  PractitionerSettingsSchema.extend({
    id: UUIDSchema,
  });
export type Settings = z.infer<typeof PractitionerSettingsSchema>;
export type UpdatePractitionerSettings = z.infer<
  typeof UpdatePractitionerSettingsSchema
>;

export const PractitionerUpdateStatusSchema = z.object({
  id: UUIDSchema,
  active: z.boolean(),
});
export type PractitionerUpdateStatus = z.infer<
  typeof PractitionerUpdateStatusSchema
>;

export const PractitionerGeneralSchema = GeneralSchema.omit({
  birthDate: true,
  identifierCode: true,
  identifierValue: true,
});
export type PractitionerGeneral = z.infer<typeof PractitionerGeneralSchema>;

export type PractitionerContact = z.infer<typeof ContactSchema>;

const PractitionerSchema = z.object({
  qualification: z.object({
    qualificationCode: z.string(),
    registrationNumber: z.string(),
  }),
  contact: ContactSchema,
  general: PractitionerGeneralSchema,
  active: z.boolean(),
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  external: PractitionerExternalSchema.optional(),
  settings: PractitionerSettingsSchema.optional(),
});
export type Practitioner = z.infer<typeof PractitionerSchema>;

export interface CreateInitialGroup {
  group: string;
}

export const PartialPractitionerSchema = z.object({
  qualification: QualificationFieldsSchema.partial(),
  contact: ContactGetSchema.partial(),
  general: PractitionerGeneralSchema.partial(),
  active: z.boolean(),
  external: PractitionerExternalSchema.optional(),
  settings: PractitionerSettingsSchema.optional(),
});
export type PartialPractitioner = z.infer<typeof PartialPractitionerSchema>;

export const PractitionerWithScopesSchema = PartialPractitionerSchema.extend({
  groups: DisplayReferenceSchema.array(),
  organizations: DisplayReferenceSchema.array(),
  assignedPatients: DisplayReferenceSchema.array(),
});
export type PractitionerWithScopes = z.infer<
  typeof PractitionerWithScopesSchema
>;

export const PractitionerGetOneResponseSchema =
  PractitionerWithScopesSchema.extend({
    id: UUIDSchema,
  });
export type PractitionerGetOneResponse = z.infer<
  typeof PractitionerGetOneResponseSchema
>;

export const PartialPractitionerWithIdSchema = PartialPractitionerSchema.extend(
  {
    id: UUIDSchema,
  },
);

export const PractitionerGetResponseSchema = PartialPractitionerWithIdSchema;
export type PractitionerGetResponse = z.infer<
  typeof PractitionerGetResponseSchema
>;

export type PractitionerCreateResponse = PractitionerGetResponse;

export type PractitionerGeneralUpdateResponse = PractitionerGetResponse;

export const PractitionerContactUpdateRequestSchema = ContactSchema.extend({
  id: UUIDSchema,
});
export type PractitionerContactUpdateRequest = z.infer<
  typeof PractitionerContactUpdateRequestSchema
>;

export const PractitionerGeneralUpdateRequestSchema =
  PractitionerGeneralSchema.extend({
    id: UUIDSchema,
  });
export type PractitionerGeneralUpdateRequest = z.infer<
  typeof PractitionerGeneralUpdateRequestSchema
>;
export type PractitionerContactUpdateResponse = PractitionerGetResponse;

export type GetQualificationResponse = PractitionerQualificationCode[];

export type QualificationUpdateRequest = z.infer<
  typeof PractitionerQualificationUpdateRequestSchema
>;
export type QualificationUpdateResponse = PractitionerGetResponse;

export type ExternalUpdateRequest = External;
export type ExternalUpdateResponse = PractitionerGetResponse;

export type SettingsUpdateRequest = Settings;
export type SettingsUpdateResponse = PractitionerGetResponse;

export type PractitionerActivateRequest = undefined;
export type PractitionerActivateResponse = undefined;

export type PractitionerDeactivateRequest = undefined;
export type PractitionerDeactivateResponse = undefined;

export const PractitionerGetAllResponseSchema = PaginatedBundleSchema.extend({
  practitioners: PractitionerGetResponseSchema.array(),
});
export type PractitionerGetAllResponse = z.infer<
  typeof PractitionerGetAllResponseSchema
>;

export const PractitionerSearchSchema = PaginationSchema.extend({
  sort: z.string().optional().describe('The field to sort by'),
  name: z.string().optional().describe('The name of the practitioner'),
  email: z.string().optional().describe('The email of the practitioner'),
  id: z
    .string()
    .optional()
    .describe('A single practitioner id or Fhir Id Query string'),
});

export const CreateInitialGroupSchema = z.object({
  group: z.string(),
});

export const ExternalSchema = z.object({
  ampathMnemonic: z.string().optional(),
});

export const SettingsSchema = z.object({
  canPrescribe: z.boolean().optional(),
});

export const PractitionerCreateSchema = z.object({
  qualification: CreateQualificationFieldsSchema,
  contact: ContactSchema,
  general: PractitionerGeneralSchema,
  initialGroup: CreateInitialGroupSchema,
  external: ExternalSchema.optional(),
  settings: SettingsSchema.optional(),
});

export type PractitionerSearch = z.infer<typeof PractitionerSearchSchema>;
export type PractitionerCreateRequest = z.infer<
  typeof PractitionerCreateSchema
>;
export type PartialPractitionerWithId = z.infer<
  typeof PartialPractitionerWithIdSchema
>;

/**
 * @deprecated Please use PractitionerSearch instead
 */
export type PractitionerGetAllParams = PractitionerSearch;
